import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import ElementUI from 'element-ui';
import 'element-ui/lib/theme-chalk/index.css';
import locale from 'element-ui/lib/locale/lang/ja';
import Axios, { AxiosRequestConfig } from 'axios';
import { polyfill } from 'es6-promise';

import '@/assets/css/timepicker.scss';
import { VueEditor } from 'vue2-editor';
import VueQriously from 'vue-qriously';
import DialogWrapper from './components/dialogWrapper.vue';
import PageContent from './components/pageContent.vue';
import ItemSelect from './components/itemSelect.vue';
import TimePicker from './components/timePicker.vue';
import StudentSelectDialog from './components/student/studentSelectDialog.vue';
import ImageDialog from './components/imageDialog.vue';
import BillTypeRadioGroup from './components/parts/billTypeRadioGroup.vue';
import adminAuth from './modules/admin/auth';
import auth from './modules/auth';
import VueMoment from 'vue-moment';
import 'moment/locale/ja';
import WindowStatePlugin from './plugins/windowStatePlugin';
import moment from 'moment';

import '@fortawesome/fontawesome-free-webfonts/css/fontawesome.css';
import '@fortawesome/fontawesome-free-webfonts/css/fa-solid.css';
import '@fortawesome/fontawesome-free-webfonts/css/fa-regular.css';
import { UserTypes } from '@/enums/userTypes';
import { SystemConfig } from '@/models/systemConfig';
import { BillTypes, getBillTypeName } from '@/enums/billTypes';

Vue.use(ElementUI, { locale });
Vue.use(VueQriously);
Vue.use(VueMoment);
Vue.use(WindowStatePlugin, {
  mini: 768,
  small: 1024,
  large: Infinity
});
Vue.config.productionTip = false;

Vue.component('vue-editor', VueEditor);
Vue.component('dialog-wrapper', DialogWrapper);
Vue.component('page-content', PageContent);
Vue.component('item-select', ItemSelect);
Vue.component('timepicker', TimePicker);
Vue.component('student-select-dialog', StudentSelectDialog);
Vue.component('image-dialog', ImageDialog);
Vue.component('bill-type-radio-group', BillTypeRadioGroup);

Vue.filter('formatNumber', (value: any) => {
  if (value === undefined || value === null) {
    return '';
  }
  if (!/^-*\d+(?:\.\d+)?$/.test(value)) {
    return value;
  }
  const formatter = new Intl.NumberFormat('ja-JP');
  return formatter.format(value);
});

Vue.filter('formatSlashDate', (date: any) => {
  if (!date) {
    return '';
  } else {
    return moment(date).format('YYYY/MM/DD');
  }
});

Vue.filter('formatSlashDateTime', (date: any) => {
  if (!date) {
    return '';
  } else {
    return moment(date).format('YYYY/MM/DD HH:mm');
  }
});

Vue.filter('formatJapaneseDate', (date: any) => {
  if (!date) {
    return '';
  } else {
    return moment(date).format('YYYY年M月D日');
  }
});

Vue.filter('formatBillType', (value: BillTypes, isPaypal: boolean, noneText: string = '未指定') => {
  return getBillTypeName(value, isPaypal, noneText);
});

polyfill();

let apiBaseUrl: string | undefined | null = null;
Axios.get('/system/config').then((response) => {
  const systemConfig = response.data as SystemConfig;
  if (!!systemConfig && !!systemConfig.apiServer) {
    apiBaseUrl = systemConfig.apiServer;
    Vue.prototype.creditcardTokenUrl = systemConfig.creditcardTokenUrl;
    Vue.prototype.dummyCreditcardNumber = systemConfig.dummyCreditcardNumber;
    Vue.prototype.dummyCreditcardSecurityCode = systemConfig.dummyCreditcardSecurityCode;
  } else {
    apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
    Vue.prototype.creditcardTokenUrl = process.env.VUE_APP_CREDITCARD_TOKEN_URL;
    Vue.prototype.dummyCreditcardNumber = process.env.VUE_APP_DUMMY_CREDITCARD_NUMBER;
    Vue.prototype.dummyCreditcardSecurityCode = process.env.VUE_APP_DUMMY_CREDITCARD_SECURITYCODE;
  }
}).catch((error) => {
  apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
  Vue.prototype.creditcardTokenUrl = process.env.VUE_APP_CREDITCARD_TOKEN_URL;
  Vue.prototype.dummyCreditcardNumber = process.env.VUE_APP_DUMMY_CREDITCARD_NUMBER;
  Vue.prototype.dummyCreditcardSecurityCode = process.env.VUE_APP_DUMMY_CREDITCARD_SECURITYCODE;
});

Axios.interceptors.request.use(async (config) => {
  if (!!config.url && /^\/system\/config/i.test(config.url)) {
    config.baseURL = undefined;
  } else {
    while (apiBaseUrl === null) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (!!config.url && /^\/api\//i.test(config.url)) {
      if (/^\/admin/i.test(document.location.pathname)) {
        config.headers.Authorization = `Bearer ${adminAuth.state.token}`;
      } else {
        config.headers.Authorization = `Bearer ${auth.state.token}`;
      }
    }

    config.baseURL = apiBaseUrl;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

Axios.interceptors.response.use(
  (response) => {
    return response;
  }, async (error) => {
    const value = error.response;
    if (!value) {
      if (error.config.dontDisplayErrorPage) {
        return Promise.reject(error);
      }
      if (/^\/admin/i.test(router.currentRoute.path)) {
        router.push({
          name: 'AdminError',
          params: {
            status: '500',
            statusText: 'システムエラー',
          },
        });
      } else {
        router.push({
          name: 'Error',
          params: {
            status: '500',
            statusText: 'システムエラー',
          },
        });
      }
      return;
    }

    if (!value) {
      if (/^\/admin/i.test(router.currentRoute.path)) {
        router.push({
          name: 'AdminError',
        });
      } else {
        router.push({
          name: 'Error',
        });
      }
      return;
    }

    switch (value.status) {
      case 400:
      case 404:
        break;
      case 401:
        if (/^\/admin/i.test(router.currentRoute.path)) {
          if (/\/api\//i.test(value.config.url)) {
            // 他APIでtoken取得中により待機
            while (adminAuth.state.pending) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
            let authorization: string | null = `Bearer ${adminAuth.state.token}`;

            // tokenがすでに変更されているか確認
            if (error.config.headers.Authorization === authorization) {
              // refreshTokenでtoken取得
              try {
                await store.dispatch('adminAuth/refreshToken');
                authorization = `Bearer ${adminAuth.state.token}`;
              } catch (error) {
                authorization = null;
              }
            }
            if (!!authorization) {
              // 新しいtokenでリトライ
              error.config.headers.Authorization = authorization;
              return Axios(error.config);
            }
          }
          router.replace({ name: 'AdminLogin' });
        } else {
          if (/\/api\//i.test(value.config.url)) {
            while (auth.state.pending) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
            let authorization: string | null = `Bearer ${auth.state.token}`;
            if (error.config.headers.Authorization === authorization) {
              if (!auth.state.token) {
                authorization = null;
              } else {
                try {
                  await store.dispatch('auth/refreshToken');
                  authorization = `Bearer ${auth.state.token}`;
                } catch (error) {
                  authorization = null;
                }
              }
            }
            if (!!authorization) {
              error.config.headers.Authorization = authorization;
              return Axios(error.config);
            }
          }
          router.replace({ name: 'Login' });
        }
        return;
      default:
        if (error.config.dontDisplayErrorPage) {
          return Promise.reject(error);
        }
        if (/^\/admin/i.test(router.currentRoute.path)) {
          router.push({
            name: 'AdminError',
            params: {
              status: value.status,
              statusText: value.statusText,
            },
          });
        } else {
          router.push({
            name: 'Error',
            params: {
              status: value.status,
              statusText: value.statusText,
            },
          });
        }
        return;
    }
    return Promise.reject(error);
  }
);

router.beforeEach((to, from, next) => {
  if (/^\/admin/i.test(to.path)) {
    if (to.name === 'AdminLogin') {
      next();
    } else {
      if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (!adminAuth.state.loggedIn) {
          next({ name: 'AdminLogin' });
        } else {
          switch (adminAuth.getters.userType(adminAuth.state)) {
            case UserTypes.Admin:
            case UserTypes.Owner:
              next();
              break;
            default:
              next({ name: 'AdminLogin' });
              break;
          }
        }
      } else {
        next();
      }
    }
  } else {
    if (to.name === 'Login') {
      next();
    } else {
      if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (!auth.state.loggedIn) {
          next({ name: 'Login' });
        } else {
          switch (auth.getters.userType(auth.state)) {
            case UserTypes.Admin:
            case UserTypes.Owner:
            case UserTypes.AdminTutor:
            case UserTypes.Tutor:
              store.commit('itemSelect/clearCache');
              next();
              break;

            case UserTypes.Student:
              next();
              break;

            default:
              next({ name: 'Login' });
              break;
          }
        }
      } else {
        next();
      }
    }
  }
});

new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount('#app');
